import {
  ProfileEntitiesObject,
  ProfileRecord,
  ProfileSwitchState,
  ProfileSwitchTableDataWithDropdown,
  ProfileTypeKeys,
} from '@/redux/slices/profileSwitchSlice';
import { AuthAPI } from '@/services/api/auth';
import { checkIndicator } from '@/utilities/utilities';
import { LocalStorageService } from 'br2s-ui-common-utils-02';

export const toggleEntityChange = (value = 'true') => {
  localStorage.setItem('entityChanged', value);
};

export const isProfileSwitchNavigateDenied = () => {
  if (
    !LocalStorageService.hasAuth() ||
    localStorage.getItem('exemptSwitchProfileDeny')
  )
    return false;

  const { sourceUen, origin } = LocalStorageService.getAuthInfo(
    'sourceUen',
    'origin',
  );
  const hasYIndicator = checkIndicator();

  return origin === 'CP' && !sourceUen && hasYIndicator;
};

export const getProfiles = () => {
  const profileTypes = ['ME', 'CSP', 'GC', 'IP'];
  const { orIndicator } = LocalStorageService.getAuthInfo('orIndicator');

  if (orIndicator === 'Y') profileTypes.push('OR');

  const promises = profileTypes.map((type) =>
    AuthAPI.switchProfile({
      pageNumber: 0,
      pageSize: 10,
      sortBy: 'lastUpdated',
      sortOrder: 'desc',
      profileType: type,
    }),
  );

  return {
    profileTypes,
    profiles: Promise.allSettled(promises),
  };
};

export const hasNonEmptyProfileRecords = (entities: ProfileEntitiesObject) =>
  Object.values(entities).some((data) => !!data.length);

export const generateAvailableTabKeys = (records: ProfileEntitiesObject) => {
  const keys = (Object.keys(records) as ProfileTypeKeys[]).filter(
    (key) => !!records?.[key] && !!records?.[key]?.length,
  );

  if (keys.includes('officialReceivers') && !keys.includes('officialReceiver'))
    keys.push('officialReceiver');

  return keys;
};

export const hasNoProfilesToSwitch = (entities: ProfileEntitiesObject) => {
  const myEntitiesLength =
    entities?.myEntities?.reduce(
      (entitiesLength, entity) =>
        entity.managedEntities && Array.isArray(entity.managedEntities)
          ? entitiesLength + (entity.managedEntities.length + 1)
          : entitiesLength + 1,
      0,
    ) ?? 0;
  const restProfileTypesLength = generateAvailableTabKeys(entities).filter(
    (key) => key !== 'myEntities',
  ).length;

  return myEntitiesLength <= 1 && restProfileTypesLength < 1;
};

export const transformToProfileEntitiesObject = (
  entities: ProfileSwitchState['entities'],
) => {
  const object: ProfileEntitiesObject = {};

  (Object.keys(entities) as ProfileTypeKeys[]).forEach((key) => {
    const currentEntity = entities[key] as ProfileSwitchTableDataWithDropdown;
    if (currentEntity.parentEntities) {
      object[key] = currentEntity.parentEntities;
    } else if (currentEntity.data) {
      object[key] = currentEntity.data;
    }
  });

  return object;
};

export const transformOrEntitiesToProfileRecords = (
  orEntities: Record<'uen' | 'entityName', string>[],
): ProfileRecord[] =>
  orEntities.map(({ uen, entityName }) => ({ uen, uenName: entityName }));

export const getSortByLabel = (
  sortBy: 'lastUpdated' | 'alphabetical',
  sortOrder: 'asc' | 'desc',
) => {
  return {
    'alphabetical-asc': 'Sort by: Alphabetical (A-Z)',
    'alphabetical-desc': 'Sort by: Alphabetical (Z-A)',
    'lastUpdated-desc': 'Sort by: Latest updated',
    'lastUpdated-asc': 'Sort by: Earliest updated',
  }[`${sortBy}-${sortOrder}`];
};

export const flattenMyEntities = (entities: ProfileRecord[] | null) => {
  if (!entities) return [];

  const flattenedEntities = entities.flatMap((item) =>
    item.managedEntities ? [item, ...item.managedEntities] : [item],
  );

  const uniqueEntities = [
    ...new Map(
      flattenedEntities.map((entity) => [entity.uen, entity]),
    ).values(),
  ];

  return uniqueEntities;
};

export const paginateMyEntities = (
  entities: ProfileRecord[] | null,
  page = 0,
  itemsPerPage = 10,
  searchKey = '',
  sortOrder = 'desc',
): ProfileRecord[] => {
  if (!entities) return [];

  let data = flattenMyEntities(entities);

  const startIndex = page * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  if (sortOrder === 'asc') {
    data.sort((a: { uenName: string }, b: { uenName: string }) =>
      a?.uenName?.localeCompare(b?.uenName),
    );
  } else if (sortOrder === 'desc') {
    data.sort((a: { uenName: string }, b: { uenName: string }) =>
      b?.uenName?.localeCompare(a?.uenName),
    );
  }

  if (searchKey !== '') {
    data = data.filter((item: { uen: string; uenName: string }) => {
      return (
        item.uen.toLowerCase().includes(searchKey.toLowerCase()) ||
        item.uenName.toLowerCase().includes(searchKey.toLowerCase())
      );
    });
  }

  const paginatedData = data.slice(startIndex, endIndex);

  return paginatedData as ProfileRecord[];
};
